// src/hrms/alarms/alarmEngine.ts
import { alarmItemPath, retryPendingCompletions } from "./alarmActions";
import { priorityLabel } from "./alarmModel";
import {
  closeAlarmNotificationsWhere,
  notificationPermission,
  registerAlarmWorker,
  showAlarmNotification,
} from "./alarmNotifications";
import { currentRing, missedOccurrences, nextAlarmWakeAt, parseAlarmDateTime, ringingOccurrences, type ActiveOccurrence } from "./alarmSchedule";
import { startAlarmSound, stopAlarmSound, subscribeAlarmAudio } from "./alarmSound";
import { claimRing, loadAlarms, subscribeAlarms } from "./alarmStore";
import { isElectron } from "../../utils/electron";

/**
 * Rings alarms while a TeamLio tab is open. Every tab runs one engine for the
 * signed-in account; they share the store, so they agree on what is ringing.
 *
 *  - Timing: it sleeps until the next ring (at most 30 s, so a laptop waking
 *    from sleep catches up quickly). A hidden tab's timers are throttled to
 *    about once a minute, so a ring can land up to a minute late there.
 *  - System notification: posted by exactly one tab per ring (claimRing, under
 *    a Web Lock where available), and not at all while a TeamLio tab has
 *    focus, because that tab is already showing the alarm dialog.
 *  - Sound: plays in the tab the user can see. The desktop app plays it even
 *    with its window closed to the tray: the window keeps running there
 *    (backgroundThrottling is off), and its notification is silent.
 */

const WAKE_CAP_MS = 30_000;
const WAKE_FLOOR_MS = 250;
const FOCUS_KEY = "teamlio.alarms.focus.v1";
/** A focused tab refreshes its claim at least every WAKE_CAP_MS; older than this it crashed or slept. */
const FOCUS_STALE_MS = 2 * WAKE_CAP_MS + 5_000;

const clockListeners = new Set<() => void>();

/** Ticks whenever an engine wakes, so badges and the dialog re-read "now". */
export function subscribeAlarmClock(listener: () => void) {
  clockListeners.add(listener);
  return () => {
    clockListeners.delete(listener);
  };
}

const tabId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

function writeFocus(focused: boolean) {
  try {
    if (focused) window.localStorage.setItem(FOCUS_KEY, `${tabId}:${Date.now()}`);
    else if (window.localStorage.getItem(FOCUS_KEY)?.startsWith(`${tabId}:`)) window.localStorage.removeItem(FOCUS_KEY);
  } catch {
    // Without storage every unfocused tab may notify; the tags still collapse duplicates.
  }
}

/** Whether some TeamLio tab (this one or another) has focus and so shows the alarm itself. */
function anyTabFocused(now: number) {
  if (document.hasFocus()) return true;
  try {
    const value = window.localStorage.getItem(FOCUS_KEY);
    if (!value) return false;
    const [owner, at] = value.split(":");
    return owner !== tabId && now - Number(at) < FOCUS_STALE_MS;
  } catch {
    return false;
  }
}

function withRingLock(run: () => Promise<void>): Promise<void> {
  const locks = (navigator as Navigator & { locks?: { request: (name: string, cb: () => Promise<void>) => Promise<void> } }).locks;
  if (!locks?.request) return run();
  return locks.request("teamlio-alarm-ring", run);
}

function ringBody(active: ActiveOccurrence) {
  const { alarm, occurrence } = active;
  const due = parseAlarmDateTime(alarm.item.dueAt);
  const parts = [
    occurrence.reason === "snooze" ? "Snoozed alarm" : occurrence.reason === "remind" ? "Reminder" : null,
    due === null ? null : `Due ${new Date(due).toLocaleString(undefined, { day: "numeric", hour: "numeric", minute: "2-digit", month: "short" })}`,
    `${priorityLabel(alarm.item.priority)} priority`,
  ].filter(Boolean);
  return `${parts.join(" · ")}\nClick to answer.`;
}

export function startAlarmEngine(ownerId: string): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let lastNotifiedSignature = "";

  const deliver = (active: ActiveOccurrence, notificationId: string, now: number) =>
    withRingLock(async () => {
      if (stopped || !claimRing(ownerId, notificationId, now)) return;
      await showAlarmNotification({
        body: ringBody(active),
        data: { itemKey: active.itemKey, occurrenceId: active.occurrence.id, ownerId, url: alarmItemPath(active.itemKey) },
        notificationId,
        title: `Alarm: ${active.alarm.item.title}`,
      });
    });

  // Notifications of alarms answered, switched off or finished (here or in another tab) come down.
  const tidyNotifications = (now: number) => {
    const alarms = loadAlarms(ownerId);
    const live = new Set([...ringingOccurrences(alarms, now), ...missedOccurrences(alarms, now)].map(({ itemKey, occurrence }) => `${itemKey}|${occurrence.id}`));
    const signature = [...live].sort().join(",");
    if (signature === lastNotifiedSignature) return;
    lastNotifiedSignature = signature;
    void closeAlarmNotificationsWhere((data) => data.ownerId !== ownerId || live.has(`${data.itemKey}|${data.occurrenceId}`));
  };

  const wake = () => {
    if (stopped) return;
    if (timer) clearTimeout(timer);
    const now = Date.now();
    const alarms = loadAlarms(ownerId);
    const audible = isElectron || document.visibilityState === "visible";
    if (document.hasFocus()) writeFocus(true);

    let sound: ActiveOccurrence | null = null;
    const canNotify = notificationPermission() === "granted" && !anyTabFocused(now);
    for (const active of ringingOccurrences(alarms, now)) {
      const ring = currentRing(active.itemKey, active.alarm, active.occurrence, now);
      if (!ring) continue;
      if (!sound) sound = active;
      if (canNotify) void deliver(active, ring.notificationId, now);
    }
    if (audible && sound) startAlarmSound(sound.alarm.sound);
    else stopAlarmSound();
    if (notificationPermission() === "granted") tidyNotifications(now);

    clockListeners.forEach((listener) => listener());
    const next = nextAlarmWakeAt(alarms, now);
    const delay = next === null ? WAKE_CAP_MS : Math.min(WAKE_CAP_MS, Math.max(WAKE_FLOOR_MS, next - now));
    timer = setTimeout(wake, delay);
  };

  const onFocus = () => {
    writeFocus(true);
    wake();
  };
  const onBlur = () => {
    writeFocus(false);
    // Focus left TeamLio: from now on a ring needs a system notification.
    wake();
  };
  const onOnline = () => {
    void retryPendingCompletions(ownerId).finally(wake);
  };

  const unsubscribe = subscribeAlarms(wake);
  // Sound unlocked by a click: a ringing alarm can start sounding now.
  const unsubscribeAudio = subscribeAlarmAudio(wake);
  document.addEventListener("visibilitychange", wake);
  window.addEventListener("focus", onFocus);
  window.addEventListener("blur", onBlur);
  window.addEventListener("pageshow", wake);
  window.addEventListener("online", onOnline);
  window.addEventListener("pagehide", onBlur);
  if (notificationPermission() === "granted") void registerAlarmWorker();
  if (navigator.onLine !== false) void retryPendingCompletions(ownerId).catch(() => undefined);
  wake();

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    unsubscribe();
    unsubscribeAudio();
    document.removeEventListener("visibilitychange", wake);
    window.removeEventListener("focus", onFocus);
    window.removeEventListener("blur", onBlur);
    window.removeEventListener("pageshow", wake);
    window.removeEventListener("online", onOnline);
    window.removeEventListener("pagehide", onBlur);
    writeFocus(false);
    stopAlarmSound();
  };
}
